<template>
  <div :class="classList">
    <page-title>{{ docType.plural | capitalize }}</page-title>

    <div v-if="isBaseUser && !hideError" style="clear: both">
      <callout type="warning" class="download-warning">
        <p>
          You do not have the required authorisation to upload new
          {{ this.$route.params.docType }}. Please contact
          <a
            href="mailto:Jenny.m@mft.nhs.uk"
            alt="Send an email to Jenny.m@mft.nhs.uk"
            >Jenny.m@mft.nhs.uk</a
          >
          if you require a policy to be uploaded or amended
        </p>
      </callout>
    </div>

    <download-warning />

    <tabs v-if="$route.meta.isAdmin">
      <tabs-item
        :key="adminTabs.indexOf(tab)"
        v-for="tab in adminTabs"
        :link="tab.to"
        >{{ tab.title }}</tabs-item
      >
    </tabs>
    <tabs v-else>
      <tabs-item
        :key="tabs.indexOf(tab)"
        v-for="tab in tabs"
        :link="tab.to"
        :active="!myDocsOpen"
        @click="showMyDocs = false"
        >{{ tab.title }}</tabs-item
      >
      <tabs-item
        :key="tabs.length"
        @click="showMyDocs = true"
        :active="myDocsOpen"
        v-if="showDocTab"
      >
        My {{ docType.plural | capitalize }}
        <icon name="drop-arrow" width="8" height="8"></icon>
      </tabs-item>
    </tabs>
    <tabs v-if="(showMyDocs && showDocTab) || myDocsOpen">
      <tabs-item
        :key="myDocTabs.indexOf(tab)"
        v-for="tab in myDocTabs"
        :link="tab.to.replace('admin/', '')"
        @click="showMyDocs = true"
        >{{ tab.title }}</tabs-item
      >
    </tabs>

    <loading v-if="loading" />
    <section-block v-else>
      <template slot="header">
        <filter-bar
          resource="documents"
          :search-query="searchQuery"
          :show-search="true"
          :show-filter="true"
          :show-sort="true"
          :filter-options="categories"
          filter-url-param="categories"
        ></filter-bar>
      </template>
    <!-- <template>
      <div>

      <message

      type="warning-solid c-message--center"
    >
      <icon width="25" height="25" name="alert-circle"></icon>
      <p><strong>WARNING:</strong> North Manchester General Hospital colleagues <span class="u-underline">must</span> access documentation via the PAT intranet system until further notice.<br><br><strong>SUPPORT:</strong> Support request form and FAQs can be found <span class="u-underline"><a href="/content/corporate-services/group-medical-directors-office/mft-intranet-policy-hub/policy-hub-support-requests">here</a></span>.</p>
    </message>

      </div>

    </template> -->
      <template slot="actions" v-if="$route.meta.isAdmin">
        <base-button
          :primary="true"
          @click="onClickDownloadReport(true)"
          iconBefore="download"
          v-if="userCanAdd"
        >
          <span>
            Download {{ docType.singular }} report
          </span>
        </base-button>
        <base-button
          :primary="true"
          @click="$router.push({ name: 'AdminDocumentCreate' })"
          iconBefore="document-create"
          v-if="userCanAdd"
        >
          <span>
            Add
            <span class="u-hide-mobile">a {{ docType.singular }}</span>
          </span>
        </base-button>
      </template>

      <template slot="actions" v-else>
        <base-button
          :primary="true"
          @click="onClickDownloadReport(false)"
          iconBefore="download"
          v-if="(showMyDocs && showDocTab) || myDocsOpen"
        >
          <span>
            Download {{ docType.singular }} report
          </span>
        </base-button>
        <base-button
          :primary="true"
          @click="$router.push({ name: 'DocumentCreate' })"
          iconBefore="document-create"
        >
          <span>
            Add
            <span class="u-hide-mobile">a {{ docType.singular }}</span>
          </span>
        </base-button>
      </template>

      <!-- policies-table here -->
      <documents-table @rowClick="rowClicked"></documents-table>
    </section-block>
  </div>
</template>

<script>
import router from '@/router';

import FilterBar from '@/components/FilterBar';
import DocumentsTable from '@/components/DocumentsTable';
import Callout from '@/components/Containers/BaseCallout';
import DownloadWarning from '@/components/DownloadWarning';
import UserHasPermission from '@/mixins/UserHasPermission';
import querySearch from '@/mixins/querySearch';
import { mapState } from 'vuex';


function routeCheck(docType, next) {
  if (docType === 'forms' || docType === 'policies') {
    next();
  } else {
    router.push('/');
  }
}

export default {
  name: 'documents',

  mixins: [UserHasPermission, querySearch],

  data() {
    return {
      currentDocType: null,
      searchQuery: '',
      hideWarning: false,
      permissionsChecked: false,
      showMyDocs: false,
      hideError: true, // everyone can add policies now so is this needed?
      showDocTab: false,
      loading: true,
    };
  },

  beforeCreate() {
    if (!this.$route.meta.openPanel) {
      this.$store.dispatch('documents/reset');
    }
  },

  async created() {
    const vm = this;
    const { docType, tab } = this.$route.params;

    if (!vm.$route.meta.openPanel) {
      this.$store.commit('documents/setTabFilter', tab);
    }

    let resp;

    try {
      resp = await this.$store.dispatch('documents/init', { docType });
      this.setFiltersFromQuery('documents');
      await this.$store.dispatch('categories/list');
      await this.$store.dispatch('documents/list');
      this.loading = false;
    } catch (err) {
      console.log('🚀 ~ file: Documents.vue ~ line 173 ~ created ~ err', err);
      this.loading = false;
    }
  },

  mounted() {
    // Do this when mounted so that we can get the user id from state
    if (!this.$route.meta.isAdmin) {
      if (this.$route.params.tab && this.$route.params.tab !== 'starred') {
        this.$store.commit(
          'documents/SET_USER_FILTER',
          this.$store.state.auth.currentUser.id,
        );
      } else {
        this.$store.commit('documents/SET_USER_FILTER', null);
      }
      // determine if we need to show the my docs tab
      this.$store
        .dispatch('documents/getUserDocCount')
        .then((resp) => {
          this.showDocTab = resp > 0;
        })
        .catch((err) => {
          this.showDocTab = false;
        });
    } else {
      this.$store.commit('documents/SET_USER_FILTER', null);
    }
  },

  beforeRouteEnter(to, from, next) {
    if (!isNaN(parseInt(to.params.tab))) {
      if (to.meta.isAdmin) {
        next({
          name: 'AdminDocumentPreview',
          params: { docType: to.params.docType, id: to.params.tab },
        });
        return;
      }
      next({
        name: 'DocumentPreview',
        params: { docType: to.params.docType, id: to.params.tab },
      });
      return;
    }
    routeCheck(to.params.docType, next);
  },

  beforeRouteUpdate(to, from, next) {
    routeCheck(to.params.docType, next);
  },

  methods: {
    rowClicked(dataItem) {
      if (dataItem.data.policy_id) {
        this.$router.push({
          name: this.$route.meta.isAdmin
            ? 'AdminDocumentVersionPreview'
            : 'DocumentVersionPreview',
          params: { id: dataItem.data.policy_id, versionId: dataItem.data.id },
        });
      } else if (dataItem.data.form_id) {
        this.$router.push({
          name: this.$route.meta.isAdmin
            ? 'AdminDocumentVersionPreview'
            : 'DocumentVersionPreview',
          params: { id: dataItem.data.form_id, versionId: dataItem.data.id },
        });
      } else {
        this.$router.push({
          name: this.$route.meta.isAdmin
            ? 'AdminDocumentPreview'
            : 'DocumentPreview',
          params: { id: dataItem.data.id },
        });
      }
    },
    onClickDownloadReport(isAdmin) {
      if (isAdmin) {
        console.log('Clicked Download Admin Report');
      } else {
        console.log('Clicked Download User Report');
      }
      this.$store.dispatch('documents/downloadReport', isAdmin);
    },
  },

  computed: {
    userCanAdd() {
      return this.userHasPermission(`add_${this.docType.plural}`);
    },
    ...mapState('auth', ['currentUser']),
    isBaseUser() {
      if (this.currentUser.privilege_groups) {
        const { privilege_groups } = this.currentUser;
        const mygroups = Object.keys(privilege_groups).filter(group => privilege_groups[group] === true);

        return mygroups.length == 1 && mygroups[0] == 'user';
      }
    },
    myDocsOpen() {
      return (
        this.showMyDocs ||
        (this.$route.meta.myDocsOpen && this.$route.params.tab !== 'starred')
      );
    },
    myDocTabs() {
      return this.adminTabs.map((tab) => {
        if (tab.title.includes('Approved')) {
          return {
            ...tab,
            to: `/admin/documents/${this.docType.plural}/approved`,
          };
        }
        return tab;
      });
    },
    displayWarning() {
      if (!this.permissionsChecked) {
        return { display: 'none' };
      }

      return { display: 'block' };
    },
    classList() {
      return {
        'p-documents': true,
        [`p-documents--${this.tabFilter}`]: this.tabFilter,
      };
    },

    hasPermissionToUpload() {
      if (!this.$store.state.auth.currentUser.privileges) {
        this.$store.dispatch('auth/listCurrentUser').then((resp) => {
          this.permissionsChecked = true;
          return this.$store.state.auth.currentUser.privileges[
            `add_${this.$route.params.docType}`
          ];
        });
      } else {
        this.permissionsChecked = true;
        return this.$store.state.auth.currentUser.privileges[
          `add_${this.$route.params.docType}`
        ];
      }
    },

    categories() {
      return this.$store.state.categories.categories.sort((a, b) => ((a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0)));
    },

    docType() {
      return this.$store.state.documents.docType;
    },

    adminTabs() {
      const vm = this;
      return [
        {
          to: `/admin/documents/${vm.docType.plural}`,
          title: `Approved ${vm.docType.plural}`,
        },
        {
          to: `/admin/documents/${vm.docType.plural}/pending`,
          title: 'Awaiting Approval',
        },
        {
          to: `/admin/documents/${vm.docType.plural}/review`,
          title: 'For Review',
        },
        {
          to: `/admin/documents/${vm.docType.plural}/lapsed`,
          title: `Lapsed ${vm.docType.plural}`,
        },
        {
          to: `/admin/documents/${vm.docType.plural}/rejected`,
          title: `Rejected ${vm.docType.plural}`,
        },
        {
          to: `/admin/documents/${vm.docType.plural}/archived`,
          title: `Archived ${vm.docType.plural}`,
        },
      ];
    },

    tabs() {
      const vm = this;
      return [
        {
          to: `/documents/${vm.docType.plural}`,
          title: `All ${vm.docType.plural}`,
        },
        {
          to: `/documents/${vm.docType.plural}/starred`,
          title: 'Starred',
        },
      ];
    },
  },

  components: {
    FilterBar,
    DocumentsTable,
    Callout,
    DownloadWarning,
  },
};
</script>
